import React, { useState, useCallback } from 'react';
import { X, Check, RotateCw, ZoomIn, ZoomOut } from 'lucide-react';
import Cropper from 'react-easy-crop';

interface ModernImageCropperProps {
  image: string;
  onCropComplete: (croppedImage: Blob) => void;
  onCancel: () => void;
  cropType?: 'round' | 'cover' | 'square';
}

interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

// Helper function to create cropped image
const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    // Only remote images need CORS. blob:/data: sources are same-origin and
    // never taint the canvas, and some browsers have historically choked on
    // crossOrigin being set for them.
    if (!/^(blob:|data:)/i.test(url)) {
      image.setAttribute('crossOrigin', 'anonymous');
    }
    image.src = url;
  });

const getCroppedImg = async (
  imageSrc: string,
  pixelCrop: CropArea,
  rotation = 0,
  cropType: 'round' | 'cover' | 'square' = 'square'
): Promise<Blob> => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('No 2d context');
  }

  // Set canvas size to match the crop area
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  // If there's rotation, handle it
  if (rotation !== 0) {
    const rotRad = (rotation * Math.PI) / 180;
    
    // Move to center of canvas
    ctx.translate(canvas.width / 2, canvas.height / 2);
    // Rotate
    ctx.rotate(rotRad);
    // Move back
    ctx.translate(-canvas.width / 2, -canvas.height / 2);
  }

  // Apply circular clipping for round crops
  if (cropType === 'round') {
    ctx.beginPath();
    ctx.arc(canvas.width / 2, canvas.height / 2, canvas.width / 2, 0, Math.PI * 2);
    ctx.clip();
  }

  // Draw the cropped portion of the image
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(blob!);
    }, 'image/jpeg', 0.95);
  });
};


const ModernImageCropper: React.FC<ModernImageCropperProps> = ({
  image,
  onCropComplete,
  onCancel,
  cropType = 'square',
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<CropArea | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const onCropChange = useCallback((crop: { x: number; y: number }) => {
    setCrop(crop);
  }, []);

  const onZoomChange = useCallback((zoom: number) => {
    setZoom(zoom);
  }, []);

  const onRotationChange = useCallback((rotation: number) => {
    setRotation(rotation);
  }, []);

  const onCropCompleteHandler = useCallback(
    (_croppedArea: any, croppedAreaPixels: CropArea) => {
      setCroppedAreaPixels(croppedAreaPixels);
    },
    []
  );

  const handleSave = useCallback(async () => {
    if (!croppedAreaPixels) return;
    
    setIsProcessing(true);
    try {
      const croppedImage = await getCroppedImg(image, croppedAreaPixels, rotation, cropType);
      onCropComplete(croppedImage);
    } catch (error) {
      console.error('Error cropping image:', error);
    } finally {
      setIsProcessing(false);
    }
  }, [croppedAreaPixels, image, rotation, onCropComplete, cropType]);

  const handleRotate = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 0.1, 3));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 0.1, 1));
  };

  // Get aspect ratio based on crop type
  const getAspectRatio = () => {
    switch (cropType) {
      case 'round':
        return 1; // Square for round crops
      case 'cover':
        return 2; // 2:1 for cover images
      case 'square':
      default:
        return 1; // Square
    }
  };

  // Get crop shape
  const getCropShape = () => {
    return cropType === 'round' ? 'round' : 'rect';
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div>
            <h3 className="text-xl font-bold text-gray-900">
              {cropType === 'round' ? 'Crop Profile Picture' : 
               cropType === 'cover' ? 'Crop Cover Image' : 
               'Crop Image'}
            </h3>
            <p className="text-sm text-gray-500 mt-1">Adjust your image to the perfect size</p>
          </div>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-gray-100 rounded-full transition-all duration-200"
            disabled={isProcessing}
          >
            <X size={20} className="text-gray-600" />
          </button>
        </div>

        {/* Cropper Area */}
        <div className="flex-1 relative bg-gray-50 min-h-[400px]">
          <Cropper
            image={image}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={getAspectRatio()}
            cropShape={getCropShape()}
            onCropChange={onCropChange}
            onZoomChange={onZoomChange}
            onRotationChange={onRotationChange}
            onCropComplete={onCropCompleteHandler}
            style={{
              containerStyle: {
                width: '100%',
                height: '100%',
                backgroundColor: '#f9fafb',
              },
            }}
          />
        </div>

        {/* Controls */}
        <div className="p-6 border-t border-gray-100 space-y-4">
          {/* Zoom Control */}
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-gray-700 w-12">Zoom</span>
            <button
              onClick={handleZoomOut}
              className="p-2 hover:bg-gray-100 rounded-full transition-all duration-200"
              disabled={zoom <= 1 || isProcessing}
            >
              <ZoomOut size={16} className="text-gray-600" />
            </button>
            <input
              type="range"
              min={1}
              max={3}
              step={0.1}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              disabled={isProcessing}
            />
            <button
              onClick={handleZoomIn}
              className="p-2 hover:bg-gray-100 rounded-full transition-all duration-200"
              disabled={zoom >= 3 || isProcessing}
            >
              <ZoomIn size={16} className="text-gray-600" />
            </button>
          </div>

          {/* Rotation Control */}
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-gray-700 w-12">Rotate</span>
            <button
              onClick={handleRotate}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-all duration-200"
              disabled={isProcessing}
            >
              <RotateCw size={16} className="text-gray-600" />
              <span className="text-sm text-gray-700">90°</span>
            </button>
            <div className="flex-1 text-sm text-gray-500">
              Current: {rotation}°
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={onCancel}
              className="flex-1 px-6 py-3 border border-gray-200 rounded-xl font-medium text-gray-700 hover:bg-gray-50 transition-all duration-200"
              disabled={isProcessing}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!croppedAreaPixels || isProcessing}
              className="flex-1 px-6 py-3 bg-gray-900 hover:bg-gray-800 text-white rounded-xl font-medium transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isProcessing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white"></div>
                  Processing...
                </>
              ) : (
                <>
                  <Check size={18} />
                  Apply Crop
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModernImageCropper;
